import { useCallback } from 'react'
import { useLocation, useNavigate, type NavigateOptions, type To } from 'react-router-dom'
import { logger } from '@upadelmatch/core'

/** Where `navigate(-1)` goes when there is no in-app history to pop. */
const DEFAULT_BACK = '/partidos'

export interface NavOptions extends NavigateOptions {
  /** Destination when `navigate(-1)` has nothing to pop. Defaults to the feed. */
  fallback?: string
}

/**
 * `useNavigate` for click handlers, with the empty-history case handled ONCE.
 *
 * React Router 7's `navigate()` returns a Promise, so every
 * `onClick={() => navigate('/x')}` is a promise handed to an attribute that
 * expects void. Rather than sprinkle `void` through the JSX or switch off
 * `no-misused-promises` for attributes (which would also stop flagging the
 * handlers that genuinely matter — join, leave, cancel, send-feedback), this
 * returns a void-returning navigate. Navigation rejection isn't actionable in a
 * click handler, but it shouldn't be an UNHANDLED rejection either.
 *
 * **`navigate(-1)` is `history.back()`, and on a deep link there is nothing of
 * ours behind it.** That is not an edge case: a fresh tab is what a WhatsApp
 * link, a push-notification tap and an OS "open in app" all produce. The button
 * either does nothing or ejects the player out of the site — measured
 * 2026-08-09, where cancelling a match from a fresh tab landed on
 * **`about:blank`**, and saving Editar perfil did the same.
 *
 * The guard lives HERE rather than at each call site because it was written at
 * one — TopBar's back chevron — and the other four were left behind, which is
 * how the same defect shipped four more times. React Router stamps
 * `key: 'default'` on the first entry of a history stack, so
 * `location.key !== 'default'` distinguishes "navigated here" from "landed
 * here" without guessing at `history.length` (which counts other sites).
 */
export function useNav(): (to: To | number, options?: NavOptions) => void {
  const navigate = useNavigate()
  const location = useLocation()

  return useCallback(
    (to: To | number, options?: NavOptions) => {
      const { fallback = DEFAULT_BACK, ...navOptions } = options ?? {}
      const canPop = location.key !== 'default'
      const target: To | number = to === -1 && !canPop ? fallback : to

      const result =
        typeof target === 'number' ? navigate(target) : navigate(target, navOptions)
      // RR7 types this as Promise<void> | void depending on the router flavour.
      void Promise.resolve(result).catch((e: unknown) => {
        logger.e('[nav] navigation failed', e)
      })
    },
    [navigate, location.key],
  )
}
